import type { EffectPayloads } from '../../effects/types';

import styles from './MessageArtifacts.module.css';

type ComparisonData = EffectPayloads['SHOW_COMPARISON_TABLE'];
type Row = ComparisonData['rows'][number];

function num(n: number | null, unit?: string): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const v = Number.isInteger(n) ? String(n) : n.toFixed(1);
  return unit ? `${v} ${unit}` : v;
}

function pct(n: number | null): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${n > 0 ? '+' : ''}${n}%`;
}

function rangeClass(pos: Row['current_in_range']): string | undefined {
  if (pos === 'below') return styles.below;
  if (pos === 'above') return styles.above;
  return undefined;
}

/** AP-451: side-by-side comparison vs the prior study. Significant rows are
 * highlighted; out-of-range current values are tinted. */
export function ComparisonTable({ data }: { data: ComparisonData }) {
  const rows = data.rows ?? [];
  if (rows.length === 0) {
    return (
      <div className={styles.artifact}>
        <div className={styles.title}>Comparison vs prior</div>
        <div className={styles.empty}>No shared measurements to compare.</div>
      </div>
    );
  }

  return (
    <div className={styles.artifact}>
      <div className={styles.title}>
        Comparison vs prior{' '}
        {data.significant_count > 0 && (
          <span className={styles.unit}>
            ({data.significant_count} significant)
          </span>
        )}
      </div>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Measurement</th>
            <th>Current</th>
            <th>Prior</th>
            <th>Δ</th>
            <th>%Δ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.key}
              className={r.clinically_significant ? styles.sigRow : undefined}
            >
              <td>{r.name}</td>
              <td className={rangeClass(r.current_in_range)}>
                {num(r.current, r.unit)}
              </td>
              <td>{num(r.prior, r.unit)}</td>
              <td>{num(r.delta)}</td>
              <td>{pct(r.pct_change)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
